import type { ButtonHTMLAttributes, JSX } from 'react';
import { Icon, type IconName } from './Icon.js';
import './Button.css';

/**
 * Buttons name the action ("Upo na", "Tira na") — design spec §12. The 48dp
 * floor comes from .btn in global.css and is not overridable per instance.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  icon?: IconName;
  block?: boolean;
  /** Blocks input while a server round-trip is in flight; the client has no authority. */
  loading?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  block = false,
  loading = false,
  className,
  children,
  disabled,
  type,
  ...rest
}: ButtonProps): JSX.Element {
  const classes = ['btn', `btn--${variant}`];
  if (size === 'lg') classes.push('btn--lg');
  if (block) classes.push('btn--block');
  if (className !== undefined) classes.push(className);

  return (
    <button
      {...rest}
      type={type ?? 'button'}
      className={classes.join(' ')}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="btn__spinner" aria-hidden="true" /> : icon ? <Icon name={icon} size={18} /> : null}
      <span className="btn__label">{children}</span>
    </button>
  );
}
